import { useState, useCallback } from 'react';
import { createPdfBlobUrl } from '../services/pdfDocumentService';

export interface PDFDocument {
  url: string;
  name: string;
  size: number;
}

export interface ViewerState {
  numPages: number;
  currentPage: number;
  scale: number;
  rotation: number;
}

const initialViewerState: ViewerState = {
  numPages: 0,
  currentPage: 1,
  scale: 1,
  rotation: 0,
};

/**
 * Hook for managing local PDF document viewer state
 * 
 * This is for local viewing state (zoom, rotation, pages),
 * NOT for API calls. Use usePDFApi hooks for backend communication.
 */
export function usePDFDocument() {
  const [document, setDocument] = useState<PDFDocument | null>(null);
  const [viewerState, setViewerState] = useState<ViewerState>(initialViewerState);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDocument = useCallback((file: File) => {
    setIsLoading(true);
    setError(null);

    try {
      // Create blob URL for the file
      const url = createPdfBlobUrl(file);

      setDocument({
        url,
        name: file.name,
        size: file.size,
      });

      // Reset viewer state
      setViewerState(initialViewerState);
      setIsLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load PDF');
      setIsLoading(false);
    }
  }, []);

  const clearDocument = useCallback(() => {
    // Revoke blob URL to free memory
    if (document?.url.startsWith('blob:')) {
      URL.revokeObjectURL(document.url);
    }

    setDocument(null);
    setViewerState(initialViewerState);
    setError(null);
  }, [document]);

  const setNumPages = useCallback((numPages: number) => {
    setViewerState((prev) => (
      prev.numPages === numPages
        ? prev
        : { ...prev, numPages }
    ));
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    setViewerState((prev) => {
      const nextPage = Math.max(1, Math.min(page, prev.numPages));
      if (nextPage === prev.currentPage) return prev;
      return { ...prev, currentPage: nextPage };
    });
  }, []);

  const setScale = useCallback((scale: number) => {
    setViewerState((prev) => (
      prev.scale === scale
        ? prev
        : { ...prev, scale }
    ));
  }, []);

  const setRotation = useCallback((rotation: number) => {
    const normalized = ((rotation % 360) + 360) % 360;
    setViewerState((prev) => (
      prev.rotation === normalized
        ? prev
        : { ...prev, rotation: normalized }
    ));
  }, []);

  return {
    document,
    viewerState,
    isLoading,
    error,
    loadDocument,
    clearDocument,
    setNumPages,
    setCurrentPage,
    setScale,
    setRotation,
  };
}
